import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { auditAPI } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Table, TableHead, TableRow, TableHeader, TableCell, TableBody } from "@/components/ui/table";

interface AuditLogEntry {
	id: number;
	userId: number;
	action: string;
	entityType: string;
	entityId: string | null;
	oldValue: Record<string, unknown> | null;
	newValue: Record<string, unknown> | null;
	ip: string | null;
	userAgent: string | null;
	createdAt: string;
	user?: { firstName?: string; lastName?: string; email?: string };
}

const AuditLogPage: React.FC = () => {
	const { t } = useTranslation();
	const { isRTL } = useLanguage();
	const [logs, setLogs] = useState<AuditLogEntry[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [entityType, setEntityType] = useState("");
	const [action, setAction] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	useEffect(() => {
		fetchLogs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, entityType, action, startDate, endDate]);

	const fetchLogs = async () => {
		try {
			setIsLoading(true);
			const data = await auditAPI.getAll({
				page,
				limit: itemsPerPage,
				entityType: entityType || undefined,
				action: action || undefined,
				startDate: startDate || undefined,
				endDate: endDate || undefined,
			});
			setLogs(data?.logs ?? []);
			setTotal(data?.total ?? 0);
		} catch {
			setLogs([]);
			setTotal(0);
		} finally {
			setIsLoading(false);
		}
	};

	const totalPages = Math.ceil(total / itemsPerPage) || 1;

	const clearFilters = () => {
		setEntityType("");
		setAction("");
		setStartDate("");
		setEndDate("");
		setPage(1);
	};

	return (
		<div className={`space-y-6 p-6 ${isRTL ? "text-right" : "text-left"}`}>
			<PageHeader
				title={t("audit.title", "Audit Log")}
				description={t("audit.description", "View system activity and changes.")}
			/>
			<Card>
				<CardContent className="pt-5">
					<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
						<div className="flex flex-col gap-2">
							<Label>{t("audit.entityType", "Entity type")}</Label>
							<Input
								aria-label={t("audit.entityType", "Entity type")}
								placeholder={t("audit.placeholders.entityType")}
								value={entityType}
								onChange={e => setEntityType(e.target.value)}
								className="h-12"
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label>{t("audit.action", "Action")}</Label>
							<Input
								aria-label={t("audit.action", "Action")}
								placeholder={t("audit.placeholders.action")}
								value={action}
								onChange={e => setAction(e.target.value)}
								className="h-12"
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label>{t("audit.startDate", "From date")}</Label>
							<Input
								aria-label={t("audit.startDate", "From date")}
								type="date"
								value={startDate}
								onChange={e => setStartDate(e.target.value)}
								className="h-12"
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label>{t("audit.endDate", "To date")}</Label>
							<Input
								aria-label={t("audit.endDate", "To date")}
								type="date"
								value={endDate}
								onChange={e => setEndDate(e.target.value)}
								className="h-12"
							/>
						</div>
						<div className="flex flex-col gap-2 justify-center items-center">
							<span className="hidden lg:block h-5" aria-hidden="true" />
							{entityType || action || startDate || endDate ? (
								<Button variant="link" className="text-destructive" onClick={clearFilters}>
									<X className={`h-4 w-4 mx-2`} />
									{t("common.clearFilters", "Clear filters")}
								</Button>
							) : (
								<Button variant="default" className="w-full" onClick={fetchLogs}>
									<Filter className={`h-4 w-4 mx-2`} />
									{t("common.apply", "Apply")}
								</Button>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			<section>
				<div>
					{isLoading ? (
						<Loading />
					) : logs.length === 0 ? (
						<p className="text-muted-foreground py-8 text-center">
							{t("audit.noEntries", "No audit entries found.")}
						</p>
					) : (
						<div>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("audit.createdAt", "Created at")}</TableHead>
										<TableHead>{t("audit.entityType", "Entity type")}</TableHead>
										<TableHead>{t("audit.action", "Action")}</TableHead>
										<TableHead>{t("audit.entityId", "Entity ID")}</TableHead>
										<TableHead>{t("audit.user", "User")}</TableHead>
										<TableHead>{t("audit.oldValue", "Old value")}</TableHead>
										<TableHead>{t("audit.newValue", "New value")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{logs.map(log => (
										<TableRow key={log.id}>
											<TableCell className="whitespace-nowrap tabular-nums">
												<bdi>{formatDate(log.createdAt)}</bdi>
											</TableCell>
											<TableCell>
												<Badge variant="outline">{log.entityType}</Badge>
											</TableCell>
											<TableCell>
												<Badge variant="secondary" className="whitespace-nowrap">
													<bdi>{log.action}</bdi>
												</Badge>
											</TableCell>
											<TableCell>
												{log.entityId && (
													<span className="text-muted-foreground whitespace-nowrap">
														<bdi>#{log.entityId}</bdi>
													</span>
												)}
											</TableCell>
											<TableCell>
												{log.user && (
													<span className="text-muted-foreground">
														{log.user.firstName} {log.user.lastName}
													</span>
												)}
											</TableCell>
											<TableCell>
												{log.oldValue && Object.keys(log.oldValue).length > 0 && (
													<details className="w-full min-w-32 max-w-64 text-start">
														<summary className="cursor-pointer py-2 text-primary">
															{t("audit.viewValue", "View value")}
														</summary>
														<pre
															dir="ltr"
															className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground [overflow-wrap:anywhere]"
														>
															{JSON.stringify(log.oldValue, null, 2)}
														</pre>
													</details>
												)}
											</TableCell>
											<TableCell>
												{log.newValue && Object.keys(log.newValue).length > 0 && (
													<details className="w-full min-w-32 max-w-64 text-start">
														<summary className="cursor-pointer py-2 text-primary">
															{t("audit.viewValue", "View value")}
														</summary>
														<pre
															dir="ltr"
															className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground [overflow-wrap:anywhere]"
														>
															{JSON.stringify(log.newValue, null, 2)}
														</pre>
													</details>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
					<Pagination
						currentPage={page}
						totalPages={totalPages}
						onPageChange={setPage}
						onPageSizeChange={setItemsPerPage}
						itemsPerPage={itemsPerPage}
						itemName="audit.pagination.entries"
						totalItems={total}
					/>
				</div>
			</section>
		</div>
	);
};

export default AuditLogPage;
